import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import './HeroSlider.css';
import '../../styles/design-tokens.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
const AUTOPLAY_MS = 6000;

/**
 * Determine the layout variant for a slide based on available assets.
 *
 * Variants:
 *  - "video-split" → video portrait TikTok bên phải + text trái
 *  - "split"       → foreground image + text (default)
 *  - "bg-only"     → background image, no foreground image
 *  - "media-only"  → foreground image, minimal text
 *  - "text-heavy"  → lots of text, small or no visual
 */
function getSlideLayout(promo, index) {
  // Slide đầu tiên luôn là video-split nếu có video_url
  if (index === 0 && promo.video_url) return 'video-split';

  const hasForeground = !!promo.image_url;
  const hasBackground = !!promo.image_secondary;
  const hasTitle = !!promo.title;
  const hasSubtitle = !!promo.subtitle;
  const hasCTA = !!(promo.primary_cta || promo.cta_text);

  if (hasBackground && !hasForeground) return 'bg-only';
  if (hasForeground && !hasSubtitle && !hasCTA) return 'media-only';
  if (hasTitle && hasSubtitle && promo.subtitle.length > 80) return 'text-heavy';

  return 'split';
}

const HeroSlider = () => {
  const [promotions, setPromotions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [progress, setProgress] = useState(0);
  const navigate = useNavigate();
  const autoPlayRef = useRef(null);
  const progressRef = useRef(null);
  const videoRef = useRef(null);

  const prefersReducedMotion =
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  useEffect(() => {
    fetchPromotions();
  }, []);

  const fetchPromotions = async () => {
    try {
      const response = await fetch(`${API_URL}/promotions/active`);
      const data = await response.json();
      if (Array.isArray(data)) {
        setPromotions(data);
      } else if (data.data && Array.isArray(data.data)) {
        setPromotions(data.data);
      } else {
        setPromotions([]);
      }
    } catch (error) {
      console.error('Error fetching promotions:', error);
      setPromotions([]);
    }
  };

  const nextSlide = useCallback(() => {
    if (promotions.length > 1) {
      setCurrentIndex((prev) => (prev + 1) % promotions.length);
      setProgress(0);
    }
  }, [promotions.length]);

  const prevSlide = useCallback(() => {
    if (promotions.length > 1) {
      setCurrentIndex((prev) => (prev - 1 + promotions.length) % promotions.length);
      setProgress(0);
    }
  }, [promotions.length]);

  const goToSlide = (index) => {
    setCurrentIndex(index);
    setProgress(0);
    setIsPaused(true);
    setTimeout(() => setIsPaused(false), 10000);
  };

  // Autoplay + progress bar
  useEffect(() => {
    if (prefersReducedMotion || isPaused || isHovered || promotions.length <= 1) {
      clearInterval(autoPlayRef.current);
      clearInterval(progressRef.current);
      return;
    }

    const startTime = Date.now();
    progressRef.current = setInterval(() => {
      const elapsed = Date.now() - startTime;
      setProgress(Math.min((elapsed / AUTOPLAY_MS) * 100, 100));
    }, 50);

    autoPlayRef.current = setInterval(() => {
      nextSlide();
    }, AUTOPLAY_MS);

    return () => {
      clearInterval(autoPlayRef.current);
      clearInterval(progressRef.current);
    };
  }, [nextSlide, isPaused, isHovered, prefersReducedMotion, promotions.length, currentIndex]);

  // Auto play video khi slide active
  useEffect(() => {
    const currentPromo = promotions[currentIndex];
    if (currentPromo?.video_url && videoRef.current) {
      videoRef.current.play().catch(() => {});
    }
  }, [currentIndex, promotions]);

  // Touch handlers
  const handleTouchStart = (e) => setTouchStart(e.targetTouches[0].clientX);
  const handleTouchMove = (e) => setTouchEnd(e.targetTouches[0].clientX);
  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    if (distance > 50) { nextSlide(); setIsPaused(true); }
    if (distance < -50) { prevSlide(); setIsPaused(true); }
    setTouchStart(0);
    setTouchEnd(0);
  };

  const handleCTA = (promo) => {
    if (promo.link_url) navigate(promo.link_url);
  };

  // Check if current active slide has a background image
  const activeHasBg = useMemo(() => {
    if (!promotions.length) return false;
    return !!promotions[currentIndex]?.image_secondary;
  }, [promotions, currentIndex]);

  return (
    <section
      className={`hero-slider-editorial${activeHasBg? ' hero-slider-editorial--has-bg-active' : ''}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      role="region"
      aria-roledescription="carousel"
      aria-label="Homepage promotions"
    >
      {promotions.map((promo, index) => {
        const layout = getSlideLayout(promo, index);
        const hasBg = !!promo.image_secondary;
        const hasForeground = !!promo.image_url;
        const isVideoSlide = layout === 'video-split';

        return (
          <div
            key={promo.id}
            className={`hero-slide ${index === currentIndex? 'active' : ''} ${
              prefersReducedMotion? 'no-animation' : ''
            } ${hasBg? 'hero-slide--has-bg' : ''} ${isVideoSlide? 'hero-slide--video' : ''}`}
            style={!hasBg && !isVideoSlide? {
              background: promo.background_gradient || 'var(--bg-hero)'
            } : undefined}
            role="group"
            aria-roledescription="slide"
            aria-label={`Slide ${index + 1} of ${promotions.length}: ${promo.title}`}
            aria-hidden={index !== currentIndex}
          >
            {/* Background image layer */}
            {hasBg && (
              <div className="hero-bg-layer">
                <img
                  src={promo.image_secondary}
                  alt=""
                  className="hero-bg-image"
                  loading={index === 0? 'eager' : 'lazy'}
                  aria-hidden="true"
                />
                <div className={`hero-bg-overlay ${
                  hasForeground? 'hero-bg-overlay--gradient' : 'hero-bg-overlay--light'
                }`} />
              </div>
            )}

            {/* Content */}
            <div className={`hero-slide-inner hero-slide-inner--${layout}`}>
              {/* Text */}
              <div className="hero-text-content">
                {promo.badge && (
                  <div className="hero-badge-pill">{promo.badge}</div>
                )}
                <h2 className="hero-headline">{promo.title}</h2>
                {promo.subtitle && (
                  <p className="hero-subheadline">{promo.subtitle}</p>
                )}
                <div className="hero-cta-buttons">
                  {(promo.primary_cta || promo.cta_text) && (
                    <button
                      className="btn-primary-hero"
                      onClick={() => handleCTA(promo)}
                    >
                      {promo.primary_cta || promo.cta_text}
                      <span className="btn-arrow">→</span>
                    </button>
                  )}
                  {promo.secondary_cta && (
                    <button
                      className="btn-secondary-hero"
                      onClick={() => handleCTA(promo)}
                    >
                      {promo.secondary_cta}
                    </button>
                  )}
                </div>
              </div>

              {/* Visual: Video hoặc Image */}
              {isVideoSlide? (
                <div className="hero-visual-content hero-visual-content--video">
                  <div className="video-wrapper-portrait">
                    <video
                      ref={index === currentIndex? videoRef : null}
                      src={promo.video_url}
                      poster={promo.video_poster || promo.image_url}
                      loop
                      muted
                      playsInline
                      className="hero-video-portrait"
                    />
                    <div className="video-glow"></div>
                    <div className="video-decor-circle video-decor-1"></div>
                    <div className="video-decor-circle video-decor-2"></div>
                  </div>
                </div>
              ) : hasForeground && layout !== 'bg-only' && (
                <div className="hero-visual-content">
                  <img
                    src={promo.image_url}
                    alt={promo.title}
                    className="hero-campaign-image"
                    loading={index === 0? 'eager' : 'lazy'}
                  />
                </div>
              )}
            </div>
          </div>
        );
      })}

      {/* Navigation Arrows */}
      {promotions.length > 1 && (
        <>
          <button
            className="hero-arrow hero-arrow-prev"
            onClick={(e) => { e.stopPropagation(); prevSlide(); setIsPaused(true); }}
            aria-label="Previous slide"
          >
            ‹
          </button>
          <button
            className="hero-arrow hero-arrow-next"
            onClick={(e) => { e.stopPropagation(); nextSlide(); setIsPaused(true); }}
            aria-label="Next slide"
          >
            ›
          </button>
        </>
      )}

      {/* Pagination Dots */}
      {promotions.length > 1 && (
        <div className="hero-dots" role="tablist" aria-label="Slide navigation">
          {promotions.map((_, index) => (
            <button
              key={index}
              className={`hero-dot ${index === currentIndex? 'active' : ''}`}
              onClick={(e) => { e.stopPropagation(); goToSlide(index); }}
              role="tab"
              aria-selected={index === currentIndex}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      )}

      {/* Progress bar */}
      {promotions.length > 1 && !isPaused && !isHovered && !prefersReducedMotion && (
        <div
          className="hero-progress"
          style={{ width: `${progress}%` }}
          aria-hidden="true"
        />
      )}
    </section>
  );
};

export default HeroSlider;